import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Store navigation history globally
const MAX_HISTORY = 5;
let navigationHistory = [];

export function useNavigationHistory() {
  const location = useLocation();
  
  useEffect(() => {
    // Don't add duplicate consecutive entries
    if (navigationHistory.length === 0 || navigationHistory[navigationHistory.length - 1] !== location.pathname) {
      navigationHistory.push(location.pathname);
      // Keep only last MAX_HISTORY entries
      if (navigationHistory.length > MAX_HISTORY + 1) {
        navigationHistory = navigationHistory.slice(-MAX_HISTORY - 1);
      }
    }
  }, [location.pathname]);
  
  return navigationHistory;
}

export function BackButton({ className = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);
  
  useEffect(() => {
    // Check if we can go back (not on dashboard and have history)
    setCanGoBack(location.pathname !== '/' && navigationHistory.length > 1);
  }, [location.pathname]);
  
  const handleBack = () => {
    // Find the previous different page
    const currentPath = location.pathname;
    let targetIndex = navigationHistory.length - 2;
    
    while (targetIndex >= 0) {
      if (navigationHistory[targetIndex] !== currentPath) {
        navigate(navigationHistory[targetIndex]);
        // Remove current and forward entries
        navigationHistory = navigationHistory.slice(0, targetIndex + 1);
        return;
      }
      targetIndex--;
    }
    
    // Fallback to browser back or dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  if (!canGoBack) return null;
  
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleBack}
      className={`text-gray-500 hover:text-gray-700 ${className}`}
    >
      <ChevronLeft className="h-4 w-4 mr-1" />
      Back
    </Button>
  );
}

export function NavigationHistoryBreadcrumb() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get last 3 unique paths (not including current)
  const uniquePaths = [];
  for (let i = navigationHistory.length - 1; i >= 0 && uniquePaths.length < 3; i--) {
    const path = navigationHistory[i];
    if (path !== location.pathname && !uniquePaths.includes(path)) {
      uniquePaths.unshift(path);
    }
  }
  
  const getPathName = (path) => {
    if (path === '/') return 'Dashboard';
    if (path.includes('/floor-plans')) return 'Floor Plans';
    if (path.includes('/estimates')) return 'Estimates';
    if (path.includes('/hub')) return 'Project Hub';
    if (path.includes('/clips')) return 'Clips Library';
    if (path.includes('/contacts')) return 'Contacts';
    if (path.includes('/projects/')) return 'Project';
    return path.split('/').pop() || 'Page';
  };
  
  if (uniquePaths.length === 0) return null;
  
  return (
    <div className="flex items-center gap-1 text-xs text-gray-400">
      {uniquePaths.map((path, i) => (
        <span key={path} className="flex items-center">
          <button
            onClick={() => navigate(path)}
            className="hover:text-gray-600 hover:underline"
          >
            {getPathName(path)}
          </button>
          {i < uniquePaths.length - 1 && <ChevronLeft className="h-3 w-3 mx-1 rotate-180" />}
        </span>
      ))}
    </div>
  );
}

export default BackButton;
